import { isPrivateKey } from '../helper'
import {
  getPrototypeOf,
  isArray,
  keys,
  mapPrototype,
  setPrototype,
  toString
} from '../shortcut'

import type { TArray, TRecord } from '../types'

export interface ToJSON {
  <T = any>(value?: T): T
  seen?: TArray | undefined
  traverse: <T = any>(value?: T, visitor?: Visitor<T>) => T
  visitor?: undefined | Visitor
}

type Visitor<T = any> = (value: T, toJSON: (value: T) => T) => T

function internalToJSON(value: any): any {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value == null
  ) {
    return value
  }
  if (typeof value !== 'object' || toJSON.seen?.includes(value))
    return undefined
  toJSON.seen?.push(value)
  try {
    if (typeof value.toJSON === 'function') return value.toJSON()
    if (typeof value.toJS === 'function') return value.toJS()
    if (isArray(value)) {
      const array: TArray = []
      for (const item of value) {
        const val = internalToJSON(item)
        array.push(val === undefined ? null : val)
      }
      return array
    }
    if (toString.call(value) === '[object Object]') {
      const object: TRecord = {}
      for (const key of keys(value)) {
        if (isPrivateKey(key)) continue
        const val = internalToJSON(value[key])
        if (val !== undefined) object[key] = val
      }
      return object
    }
    const prototype = getPrototypeOf(value)
    if (prototype === mapPrototype || prototype === setPrototype) return {}
    return undefined
  } finally {
    toJSON.seen?.pop()
  }
}

export const toJSON: ToJSON = (value) => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value == null
  ) {
    return value
  }

  const prevSeen = toJSON.seen
  toJSON.seen = prevSeen ?? []
  try {
    return toJSON.visitor
      ? toJSON.visitor(value, (value) => internalToJSON(value))
      : internalToJSON(value)
  } finally {
    toJSON.seen = prevSeen
  }
}

toJSON.traverse = (value, visitor) => {
  const prevVisitor = toJSON.visitor
  toJSON.visitor = visitor
  try {
    return toJSON(value)
  } finally {
    toJSON.visitor = prevVisitor
  }
}
